import { useState, useEffect, useRef } from 'react'
import { Accessibility, Play, Pause, Square } from 'lucide-react'
import { cn } from '@/utils/cn'
import { screenReader } from '@/utils/screenReader'
import { useTranslation } from 'react-i18next'

interface AccessibilityToolbarProps {
  className?: string
}

export default function AccessibilityToolbar({ className }: AccessibilityToolbarProps) {
  const { t } = useTranslation()
  // Load from localStorage on mount
  const [isOpen, setIsOpen] = useState(false)
  const [fontSize, setFontSize] = useState<'normal' | 'large' | 'xlarge'>(() => {
    const saved = localStorage.getItem('accessibility-font-size')
    return (saved as 'normal' | 'large' | 'xlarge') || 'normal'
  })
  const [highContrast, setHighContrast] = useState(() => {
    return localStorage.getItem('accessibility-high-contrast') === 'true'
  })
  const [screenReaderEnabled, setScreenReaderEnabled] = useState(false)
  const [isReading, setIsReading] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isUserControllingRef = useRef<boolean>(false) // Flag to prevent hover from interrupting user actions

  // Load saved settings on mount
  useEffect(() => {
    // Apply saved font size
    const savedFontSize = localStorage.getItem('accessibility-font-size')
    if (savedFontSize && ['normal', 'large', 'xlarge'].includes(savedFontSize)) {
      setFontSize(savedFontSize as 'normal' | 'large' | 'xlarge')
    }

    // Apply saved high contrast
    const savedContrast = localStorage.getItem('accessibility-high-contrast')
    if (savedContrast === 'true') {
      setHighContrast(true)
      document.documentElement.classList.add('high-contrast')
    }
  }, [])

  // Open panel when header accessibility icon is clicked (Figma: icon in header)
  useEffect(() => {
    const handler = () => setIsOpen(true)
    document.addEventListener('open-accessibility', handler)
    return () => document.removeEventListener('open-accessibility', handler)
  }, [])

  useEffect(() => {
    // Apply font size to document
    document.documentElement.classList.remove('font-normal', 'font-large', 'font-xlarge')
    if (fontSize === 'large') {
      document.documentElement.classList.add('font-large')
    } else if (fontSize === 'xlarge') {
      document.documentElement.classList.add('font-xlarge')
    }
    
    // Save to localStorage
    localStorage.setItem('accessibility-font-size', fontSize)
  }, [fontSize])

  // Screen Reader effects
  useEffect(() => {
    screenReader.setEnabled(screenReaderEnabled)
    
    if (screenReaderEnabled) {
      // Wait a bit before reading to ensure screen reader is ready
      // Also check if browser supports speech synthesis
      if (screenReader.isSupported()) {
        setTimeout(() => {
          // Only read if still enabled
          if (screenReader.getEnabled()) {
            screenReader.readPageTitle()
          }
        }, 500)
      }

      // Monitor reading state
      intervalRef.current = setInterval(() => {
        setIsReading(screenReader.getIsReading())
      }, 100)

      // Add hover listeners to read elements when hovered
      let lastHoveredElement: HTMLElement | null = null
      
      const handleMouseOver = (e: MouseEvent) => {
        const target = e.target as HTMLElement
        
        // Skip if hovering on accessibility toolbar itself or its children
        if (target.closest('.accessibility-toolbar')) {
          return
        }

        // Skip if user is controlling playback manually
        if (isUserControllingRef.current) {
          return
        }

        // Skip if same element (to prevent re-reading)
        if (target === lastHoveredElement) {
          return
        }

        // Skip audio/video elements and their containers
        if (target.closest('audio, video, [class*="radio"], [class*="audio"], [class*="player"]')) {
          return
        }

        // Skip elements marked to be ignored by screen reader
        if (target.closest('[data-screen-reader-ignore="true"]')) {
          return
        }

        // Skip iframe elements (like radio embeds)
        if (target.closest('iframe')) {
          return
        }

        lastHoveredElement = target

        // Skip empty elements or elements with no meaningful content
        const tagName = target.tagName.toLowerCase()
        if (['script', 'style', 'noscript', 'meta', 'link', 'audio', 'video', 'iframe', 'source', 'track'].includes(tagName)) {
          return
        }

        // Skip elements with data attributes indicating they're media players
        if (target.hasAttribute('data-radio') || target.hasAttribute('data-audio') || target.hasAttribute('data-player')) {
          return
        }

        // Always read on hover - this is the main feature
        // Stop current reading and read the hovered element
        screenReader.readElement(target, false)
      }

      const handleMouseOut = (e: MouseEvent) => {
        // Cancel any pending hover reads when leaving
        const relatedTarget = e.relatedTarget as HTMLElement
        
        // Only cancel if not moving to a child element
        if (!relatedTarget || !lastHoveredElement?.contains(relatedTarget)) {
          screenReader.cancelHoverRead()
          lastHoveredElement = null
        }
      }

      document.addEventListener('mouseover', handleMouseOver, true) // Use capture phase
      document.addEventListener('mouseout', handleMouseOut, true)

      return () => {
        document.removeEventListener('mouseover', handleMouseOver, true)
        document.removeEventListener('mouseout', handleMouseOut, true)
        screenReader.cancelHoverRead()
        lastHoveredElement = null
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
      }
    } else {
      screenReader.stop()
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      if (!screenReaderEnabled) {
        screenReader.stop()
      }
    }
  }, [screenReaderEnabled])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      screenReader.stop()
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  const toggleContrast = () => {
    const newValue = !highContrast
    setHighContrast(newValue)
    document.documentElement.classList.toggle('high-contrast', newValue)
    localStorage.setItem('accessibility-high-contrast', String(newValue))
  }

  const increaseFontSize = () => {
    if (fontSize === 'normal') {
      setFontSize('large')
    } else if (fontSize === 'large') {
      setFontSize('xlarge')
    }
  }

  const decreaseFontSize = () => {
    if (fontSize === 'xlarge') {
      setFontSize('large')
    } else if (fontSize === 'large') {
      setFontSize('normal')
    }
  }

  return (
    <div className={cn('fixed bottom-4 left-4 z-50 accessibility-toolbar', className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-primary-600 dark:bg-primary-500 text-white p-3 rounded-full shadow-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
        aria-label={t('accessibility.title')}
        aria-expanded={isOpen}
      >
        <Accessibility className="w-5 h-5" />
      </button>

      {isOpen && (
        <div className="absolute bottom-16 left-0 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 min-w-[200px] accessibility-toolbar">
          <div className="space-y-3">
            <div className="border-b border-gray-200 dark:border-gray-700 pb-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('accessibility.title')}</h3>
            </div>

            {/* Font Size */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 space-x-reverse">
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('accessibility.fontSize')}</span>
              </div>
              <div className="flex items-center space-x-1 space-x-reverse">
                <button
                  onClick={decreaseFontSize}
                  className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded"
                  aria-label={t('accessibility.decreaseFontSize')}
                  disabled={fontSize === 'normal'}
                  title={t('accessibility.decreaseFontSize')}
                >
                  <span className="text-xs font-normal">A</span>
                </button>
                <span className="text-xs text-gray-500 dark:text-gray-400 mx-2 min-w-[50px] text-center">
                  {fontSize === 'normal' ? (
                    <span className="text-sm font-medium text-primary-600 dark:text-primary-400">A</span>
                  ) : fontSize === 'large' ? (
                    <span className="text-base font-semibold text-primary-600 dark:text-primary-400">A</span>
                  ) : (
                    <span className="text-lg font-bold text-primary-600 dark:text-primary-400">A</span>
                  )}
                </span>
                <button
                  onClick={increaseFontSize}
                  className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded"
                  aria-label={t('accessibility.increaseFontSize')}
                  disabled={fontSize === 'xlarge'}
                  title={t('accessibility.increaseFontSize')}
                >
                  <span className="text-base font-bold">A</span>
                </button>
              </div>
            </div>

            {/* Screen Reader */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 space-x-reverse">
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('accessibility.screenReader')}</span>
                </div>
                <button
                  onClick={() => {
                    const newState = !screenReaderEnabled
                    setScreenReaderEnabled(newState)
                    if (newState && screenReader.isSupported()) {
                      // Read page title first, then main content
                      setTimeout(() => {
                        screenReader.readPageTitle()
                        setTimeout(() => {
                          screenReader.readMainContent()
                        }, 2000)
                      }, 300)
                    }
                  }}
                  className={cn(
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                    screenReaderEnabled ? 'bg-primary-600 dark:bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
                  )}
                  aria-label={t('accessibility.screenReader')}
                  role="switch"
                  aria-checked={screenReaderEnabled}
                  disabled={!screenReader.isSupported()}
                  title={screenReader.isSupported() ? t('accessibility.screenReader') : t('accessibility.notSupported')}
                >
                  <span
                    className={cn(
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      screenReaderEnabled ? 'translate-x-6' : 'translate-x-1'
                    )}
                  />
                </button>
              </div>
              
              {screenReaderEnabled && (
                <div className="flex items-center justify-end space-x-1 space-x-reverse pt-1 border-t border-gray-200 dark:border-gray-700">
                  {isReading ? (
                    <button
                      onClick={() => {
                        screenReader.pause()
                        setIsReading(false)
                      }}
                      className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded transition-colors"
                      aria-label={t('accessibility.pause')}
                      title={t('accessibility.pause')}
                    >
                      <Pause className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        // Set flag to prevent hover from interrupting - keep it true while reading
                        isUserControllingRef.current = true
                        
                        // If paused, resume; otherwise start reading main content
                        const synth = (screenReader as any).synthInstance
                        if (screenReader.getIsReading() && synth?.paused) {
                          screenReader.resume()
                        } else {
                          screenReader.readMainContent()
                        }
                        
                        setIsReading(true)
                        
                        // Keep flag true while reading, reset only after reading ends
                        // Check reading state periodically
                        const checkReading = setInterval(() => {
                          if (!screenReader.getIsReading()) {
                            clearInterval(checkReading)
                            // Wait a bit more before allowing hover again
                            setTimeout(() => {
                              isUserControllingRef.current = false
                            }, 500)
                          }
                        }, 500)
                        
                        // Safety: reset after 5 minutes max
                        setTimeout(() => {
                          clearInterval(checkReading)
                          isUserControllingRef.current = false
                        }, 300000)
                      }}
                      className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded transition-colors"
                      aria-label={t('accessibility.play')}
                      title={t('accessibility.play')}
                    >
                      <Play className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      isUserControllingRef.current = true
                      screenReader.stop()
                      setIsReading(false)
                      setTimeout(() => {
                        isUserControllingRef.current = false
                      }, 1000)
                    }}
                    className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors"
                    aria-label={t('accessibility.stop')}
                    title={t('accessibility.stop')}
                  >
                    <Square className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      isUserControllingRef.current = true
                      screenReader.readMainContent()
                      setIsReading(true)
                      setTimeout(() => {
                        isUserControllingRef.current = false
                      }, 2000)
                    }}
                    className="p-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded transition-colors border border-gray-300 dark:border-gray-600"
                    aria-label={t('accessibility.readMain')}
                    title={t('accessibility.readMain')}
                  >
                    {t('common.view')}
                  </button>
                </div>
              )}
            </div>
            {!screenReader.isSupported() && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                {t('accessibility.notSupported')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

